import { useCallback, useMemo, useState } from 'react'

import i18n from 'modules/core/lib/i18n'

import type {
  AttributeFilters,
  DeviceCatalogView,
  DistributorProductVariant,
  FilterSection,
  ProductGroup,
} from './types'
import { CATEGORY_TYPE_MAP, SAVED_IDS_STORAGE_KEY } from './types'
import { DELIVERY_TIME_OPTIONS } from './constants'

/** Static category label lookup — avoids dynamic i18n keys */
function getCategoryI18nLabel(catId: string): string {
  const labels: Record<string, () => string> = {
    accessories: () => i18n.t('it_management.device_catalog.categories.accessories'),
    audio_video: () => i18n.t('it_management.device_catalog.categories.audio_video'),
    computers: () => i18n.t('it_management.device_catalog.categories.computers'),
    mobile_phones: () => i18n.t('it_management.device_catalog.categories.mobile_phones'),
    monitors: () => i18n.t('it_management.device_catalog.categories.monitors'),
    networking_it: () => i18n.t('it_management.device_catalog.categories.networking_it'),
    tablets: () => i18n.t('it_management.device_catalog.categories.tablets'),
    wearables: () => i18n.t('it_management.device_catalog.categories.wearables'),
  }
  return labels[catId]?.() ?? catId
}

function loadSavedIds(): string[] {
  try {
    const raw = localStorage.getItem(SAVED_IDS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persistSavedIds(ids: string[]): void {
  try {
    localStorage.setItem(SAVED_IDS_STORAGE_KEY, JSON.stringify(ids))
  } catch {
    // Private browsing may throw
  }
}

export function useDeviceCatalogFilters() {
  const [view, setViewState] = useState<DeviceCatalogView>('all')
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [attributeFilters, setAttributeFilters] = useState<AttributeFilters>({})
  const [savedIds, setSavedIds] = useState<string[]>(loadSavedIds)

  const setView = useCallback((newView: DeviceCatalogView) => {
    setViewState(newView)
    if (newView !== 'category') {
      setSelectedCategory(null)
    }
  }, [])

  const setCategory = useCallback((categoryId: string | null) => {
    setSelectedCategory(categoryId)
    setViewState(categoryId ? 'category' : 'all')
  }, [])

  const setAttributeFilter = useCallback((key: string, values: string[]) => {
    setAttributeFilters((prev) => {
      if (values.length === 0) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: values }
    })
  }, [])

  const toggleSaved = useCallback((id: string) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      persistSavedIds(next)
      return next
    })
  }, [])

  const isSaved = useCallback((id: string) => savedIds.includes(id), [savedIds])

  /**
   * Applies only view-level filters (view type + category).
   * Used to derive contextual filter options.
   */
  const applyViewFilter = useCallback(
    (items: DistributorProductVariant[]): DistributorProductVariant[] => {
      let filtered = items

      if (view === 'saved') {
        filtered = filtered.filter((item) => savedIds.includes(item.id))
      } else if (view === 'offers') {
        // No offer flag available in API yet — return all for now
      } else if (view === 'zero-touch') {
        filtered = filtered.filter((item) =>
          item.productAttributes.some(
            (attr) => attr.name.toLowerCase() === 'zero-touch' && attr.value.toLowerCase() === 'yes'
          )
        )
      } else if (view === 'category' && selectedCategory) {
        const allowedTypes = CATEGORY_TYPE_MAP[selectedCategory] ?? []
        filtered = filtered.filter((item) => {
          const typeName = item.product?.typeName?.toLowerCase().trim()
          return typeName ? allowedTypes.includes(typeName) : false
        })
      }

      return filtered
    },
    [view, selectedCategory, savedIds]
  )

  const applyFilters = useCallback(
    (items: DistributorProductVariant[]): DistributorProductVariant[] => {
      // First apply view-level filters
      let filtered = applyViewFilter(items)

      // Then apply attribute filters (brand + delivery_time + product attributes)
      for (const [key, values] of Object.entries(attributeFilters)) {
        if (values.length === 0) continue

        if (key === 'brand') {
          filtered = filtered.filter((item) => {
            const brand = item.product?.brand
            return brand ? values.includes(brand) : false
          })
        } else if (key === 'delivery_time') {
          filtered = filtered.filter((item) => {
            const dc = item.deliveryConditions?.toLowerCase().trim() ?? ''
            return values.some((v) => dc.includes(v.toLowerCase()))
          })
        } else {
          filtered = filtered.filter((item) =>
            item.productAttributes.some((attr) => attr.name === key && values.includes(attr.value))
          )
        }
      }

      return filtered
    },
    [applyViewFilter, attributeFilters]
  )

  const extractFilterOptions = useCallback(
    (items: DistributorProductVariant[]): FilterSection[] => {
      // Delivery time (static options)
      const deliveryTimeSection: FilterSection = {
        id: 'delivery_time',
        label: i18n.t('it_management.device_catalog.filters.delivery_time'),
        options: DELIVERY_TIME_OPTIONS,
        selectedValues: attributeFilters['delivery_time'] ?? [],
        onChange: (values: string[]) => setAttributeFilter('delivery_time', values),
      }

      // Brand
      const brands = [
        ...new Set(
          items.map((i) => i.product?.brand).filter((b): b is string => typeof b === 'string')
        ),
      ]
      const brandSection: FilterSection = {
        id: 'brand',
        label: i18n.t('it_management.device_catalog.filters.brand'),
        options: brands.sort().map((b) => ({ value: b, label: b })),
        selectedValues: attributeFilters['brand'] ?? [],
        onChange: (values: string[]) => setAttributeFilter('brand', values),
      }

      // Product attributes
      const attrMap = new Map<string, Set<string>>()
      items.forEach((item) => {
        item.productAttributes.forEach((attr) => {
          if (!attrMap.has(attr.name)) attrMap.set(attr.name, new Set())
          const existing = attrMap.get(attr.name)
          if (existing) existing.add(attr.value)
        })
      })

      const attrSections: FilterSection[] = Array.from(attrMap.entries()).map(
        ([name, valuesSet]) => ({
          id: name,
          label: name,
          options: Array.from(valuesSet)
            .sort()
            .map((v) => ({ value: v, label: v })),
          selectedValues: attributeFilters[name] ?? [],
          onChange: (values: string[]) => setAttributeFilter(name, values),
        })
      )

      return [deliveryTimeSection, brandSection, ...attrSections]
    },
    [attributeFilters, setAttributeFilter]
  )

  const groupItems = useCallback(
    (items: DistributorProductVariant[]): ProductGroup[] => {
      if (view === 'all') {
        return [
          {
            id: 'all',
            title: i18n.t('it_management.device_catalog.views.all_devices_section'),
            description: i18n.t(
              'it_management.device_catalog.views.all_devices_section_description'
            ),
            items,
          },
        ]
      }

      if (view === 'offers') {
        return [
          {
            id: 'offers',
            title: i18n.t('it_management.device_catalog.views.current_offers'),
            description: i18n.t('it_management.device_catalog.views.offers_description'),
            items,
          },
        ]
      }

      // saved, zero-touch, category — single group
      const titleMap: Record<string, string> = {
        saved: i18n.t('it_management.device_catalog.views.saved'),
        'zero-touch': i18n.t('it_management.device_catalog.views.zero_touch'),
        category: selectedCategory ? getCategoryI18nLabel(selectedCategory) : '',
      }

      return [
        {
          id: view,
          title: titleMap[view] ?? '',
          items,
        },
      ]
    },
    [view, selectedCategory]
  )

  const state = useMemo(
    () => ({ view, search, selectedCategory, attributeFilters, savedIds }),
    [view, search, selectedCategory, attributeFilters, savedIds]
  )

  return {
    state,
    setView,
    setSearch,
    setCategory,
    setAttributeFilter,
    toggleSaved,
    isSaved,
    applyViewFilter,
    applyFilters,
    extractFilterOptions,
    groupItems,
  }
}
